import { type CollectionRepair, CollectionRepairReason } from 'serviceWorker/db/collectionRepairReason';
import { readServiceTablesSnapshot } from 'serviceWorker/db/serviceTablesSnapshot';
import { describeDatabaseSync } from 'shared/domain/serviceData/entitySync';
import { AdditionalSwLogInfo, LogLevel } from 'shared/types/logger';
import { getErrorMessage } from 'shared/utils/errorHandling';
import { swLog } from './makeSwLogger';

/**
 * The logs of the stored data: data scopes, collections of another project,
 * repaired collections, failures of the service tables. Anomalies are errors (Sentry) with the state
 * of every service table; expected events are info (Sumo), each logged once.
 * Described in `.claude/skills/h-sw-channels/data-scope.md` ("Logs").
 */

/** The state of every service table for a log; never rejects. */
async function describeServiceTables(): Promise<NonNullable<AdditionalSwLogInfo['serviceTables']>> {
  try {
    return describeDatabaseSync(await readServiceTablesSnapshot());
  } catch (error) {
    return { snapshotFailed: getErrorMessage(error) ?? String(error) };
  }
}

function errorName(error: unknown): string {
  return error instanceof Error ? error.name : typeof error;
}

export interface SkippedWrite {
  /** What the scope was started for, e.g. `sites of project 5e41…`. */
  description: string;
  owner: string;
  projectId: string | undefined;
}

/** A write of outdated data was skipped; expected after a project switch. Logged once per scope by the caller. */
export function logOutdatedWriteSkipped({ description, owner, projectId }: SkippedWrite): void {
  swLog(`Skipped a local write of outdated data (${description})`, LogLevel.INFO, null, {
    projectId,
    data: { owner },
  });
}

/** A check that is true the first time it gets a key: expected events are logged once per key. */
function createFirstReportCheck(): (key: string) => boolean {
  const reportedKeys = new Set<string>();
  return (key) => {
    if (reportedKeys.has(key)) {
      return false;
    }
    reportedKeys.add(key);
    return true;
  };
}

const isFirstReportOfAnotherProjectDownload = createFirstReportCheck();

/** A collection downloaded for another project reads as not downloaded; logged once per table and project. */
export function logDownloadOfAnotherProject(
  table: string,
  storedProjectId: string | undefined,
  configuredProjectId: string | undefined
): void {
  if (!isFirstReportOfAnotherProjectDownload(`${table}:${storedProjectId}`)) {
    return;
  }
  swLog(`Collection downloaded for another project read as missing (${table})`, LogLevel.INFO, null, {
    data: { tables: [table], storedProjectId, configuredProjectId },
  });
}

/**
 * The repair is an error when the state should never happen (a downloaded
 * table that lost its rows); otherwise expected after a stopped worker, an
 * unfinished switch or an item created elsewhere.
 */
function logLevelOfRepairs(repairs: CollectionRepair[]): LogLevel {
  return repairs.some(({ reason }) => reason === CollectionRepairReason.emptyAfterDownload)
    ? LogLevel.ERROR
    : LogLevel.INFO;
}

function describeRepairs(repairs: CollectionRepair[]): string {
  return repairs.map(({ table, reason }) => `${table}: ${reason}`).join(', ');
}

/** Collections cleared at start, so they are downloaded again. */
export async function logCollectionsRepairedAtStart(repairs: CollectionRepair[]): Promise<void> {
  const level = logLevelOfRepairs(repairs);
  swLog(
    `Cleared broken collections at start (${describeRepairs(repairs)})`,
    level,
    null,
    { data: { operation: 'start', tables: repairs.map(({ table }) => table) }, serviceTables: await describeServiceTables() },
    level === LogLevel.ERROR ? { fingerprint: ['sw-collection-repaired', 'start'] } : undefined
  );
}

/**
 * A write was still running when the project data was flushed, after the
 * wait for it ran out: it may land after the flush. An anomaly, with the
 * state of the service tables.
 */
export async function logWritesStillRunningAtFlush(waitedMs: number): Promise<void> {
  swLog(
    `A write was still running ${waitedMs} ms after the flush was requested`,
    LogLevel.ERROR,
    null,
    { data: { operation: 'flush' }, serviceTables: await describeServiceTables() },
    { fingerprint: ['sw-write-running-at-flush'] }
  );
}

const isFirstReportOfItemNotFound = createFirstReportCheck();

/**
 * An item read by id exists neither locally nor on the server (deleted, no
 * access, or lost); logged once per item. The page reports a missing
 * reference of an issue as an error.
 */
export function logItemNotFoundById(entity: string, itemId: string): void {
  if (!isFirstReportOfItemNotFound(`${entity}:${itemId}`)) {
    return;
  }
  swLog(`Item not found by id (${entity})`, LogLevel.INFO, null, { data: { tables: [entity], itemId } });
}

/**
 * A row created on this device got its server id, and a sync had stored the
 * server's copy of it first: the copy was dropped, the local row kept (PT-7278).
 * Expected when a sync runs during an upload; info, with the rows involved.
 */
export function logServerCopyOfLocalRowDropped(
  table: string,
  { localId, remoteId, droppedLocalIds }: { localId: number; remoteId: string; droppedLocalIds: number[] }
): void {
  swLog(`Server copy of a row created here dropped (${table})`, LogLevel.INFO, null, {
    data: { tables: [table], itemId: remoteId },
    query: { localId, droppedLocalIds },
  });
}

/**
 * A row created on this device was gone (flushed or removed during its upload)
 * when its server id came: nothing was changed (PT-7278). Info.
 */
export function logLocalRowGoneBeforeRemoteId(
  table: string,
  { localId, remoteId }: { localId: number; remoteId: string }
): void {
  swLog(`Row created here gone before its server id (${table})`, LogLevel.INFO, null, {
    data: { tables: [table], itemId: remoteId },
    query: { localId },
  });
}

/** A collection marked to be downloaded again in full by its next sync. */
export async function logCollectionDownloadedAgain(
  table: string,
  reason: CollectionRepairReason,
  configuredProjectId: string | undefined
): Promise<void> {
  const repairs = [{ table, reason }];
  const level = logLevelOfRepairs(repairs);
  swLog(
    `Collection downloaded again (${describeRepairs(repairs)})`,
    level,
    null,
    {
      data: { operation: reason, tables: [table], configuredProjectId },
      serviceTables: await describeServiceTables(),
    },
    level === LogLevel.ERROR ? { fingerprint: ['sw-collection-repaired', reason], tags: { table } } : undefined
  );
}

/**
 * Collections of another project are left after the project data was
 * cleared: the guard of the stored data failed somewhere.
 */
export async function logLeftoverDataOfAnotherProject(
  operation: string,
  tables: string[],
  configuredProjectId: string
): Promise<void> {
  swLog(
    `Collections of another project left after ${operation} (${tables.join(', ')})`,
    LogLevel.ERROR,
    null,
    { data: { operation, tables, configuredProjectId }, serviceTables: await describeServiceTables() },
    { fingerprint: ['sw-data-of-another-project', operation] }
  );
}

/**
 * Logs a failed read, write or clear of service tables with the state of all
 * of them, then rethrows. The state is read afterwards, outside the failed
 * transaction; the log does not delay the rejection.
 */
export function rethrowServiceTableFailure(operation: string, tables: string[]): (error: unknown) => never {
  return (error) => {
    void describeServiceTables().then((serviceTables) =>
      swLog(
        `Service table ${operation} failed (${tables.join(', ')}): ${errorName(error)}`,
        LogLevel.ERROR,
        error,
        { data: { operation, tables }, serviceTables },
        { fingerprint: ['sw-service-table', operation, errorName(error)], tags: { table: tables.join(',') } }
      )
    );
    throw error;
  };
}
